import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    IconButton,
    Flex,
    Skeleton as ChakraSkeleton,
    Text,
    useMultiStyleConfig,
    VStack,
    Heading,
} from '@chakra-ui/react'
import {useIntl} from 'react-intl'

import loadable from '@loadable/component'
const ReviewScoreIcon = loadable(() => import('../review/ReviewScoreIcon'))

import {FavouriteFilledIcon, FavouriteIcon} from '../icons'
import {useWishlistActions} from '../../hooks/use-wishlist-actions'
import {useWishList} from '../../hooks/use-wish-list'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
import {getPriceData} from '../../utils/product-utils'
import {isBundleSet} from '../../utils/bundle-utils'
import SanitizedHtml from '../html-sanitizer'
import {remCalc} from '../../theme/_utils'
import {decode} from 'html-entities'

const normalizeProductHeaderDetailsHtml = (html = '') => {
    return html
        .replace(/<li[^>]*>/gi, '<p>')
        .replace(/<\/li>/gi, '</p>')
        .replace(/<\/?ul[^>]*>/gi, '')
        .replace(/<\/?ol[^>]*>/gi, '')
}

const getProductHeaderPreviewText = (html = '') => {
    return decode(
        html
            .replace(/<\/li>/gi, '. ')
            .replace(/<li[^>]*>/gi, ' ')
            .replace(/<\/?ul[^>]*>/gi, ' ')
            .replace(/<\/?ol[^>]*>/gi, ' ')
            .replace(/<\/p>/gi, ' ')
            .replace(/<p[^>]*>/gi, ' ')
            .replace(/<br\s*\/?>/gi, ' ')
            .replace(/<[^>]+>/g, ' ')
            .replace(/&nbsp;/gi, ' ')
            .replace(/\s+/g, ' ')
            .replace(/\s*\.\s*/g, '. ')
            .trim(),
    )
}

export const Skeleton = () => {
    const style = useMultiStyleConfig('ProductHeader')
    const hideAccountPref = useBooleanCustomPreference('hideAccount')
    const hideAccount = hideAccountPref !== null ? hideAccountPref : false

    return (
        <VStack {...style.container} data-testid="product-header-skeleton" align="stretch">
            <Flex {...style.reviewWrapper}>
                <ChakraSkeleton h={remCalc(22)} w={remCalc(160)} borderRadius="full" />
            </Flex>
            <VStack {...style.contentWrapper} align="stretch">
                <Flex justify="space-between" w="100%" alignItems="flex-start">
                    <VStack align="stretch" spacing={3} flex="1">
                        <ChakraSkeleton
                            h={remCalc(40)}
                            w={{base: '74%', md: '64%'}}
                            borderRadius="xl"
                        />
                        <ChakraSkeleton h={remCalc(100)} w="100%" borderRadius="2xl" />
                        <VStack align="flex-start" spacing={1} pt={4}>
                            <ChakraSkeleton h={remCalc(11)} w={remCalc(54)} borderRadius="full" />
                            <ChakraSkeleton h={remCalc(11)} w={remCalc(84)} borderRadius="full" />
                        </VStack>
                        <Flex gap={2} pt={1}>
                            <ChakraSkeleton
                                h={{base: remCalc(40), md: remCalc(48)}}
                                w={{base: remCalc(24), md: remCalc(32)}}
                                borderRadius="full"
                            />
                            <ChakraSkeleton
                                h={{base: remCalc(40), md: remCalc(48)}}
                                w={{base: remCalc(24), md: remCalc(32)}}
                                borderRadius="full"
                            />
                            <ChakraSkeleton
                                h={{base: remCalc(40), md: remCalc(48)}}
                                w={{base: remCalc(24), md: remCalc(32)}}
                                borderRadius="full"
                            />
                            <ChakraSkeleton
                                h={{base: remCalc(40), md: remCalc(48)}}
                                w={{base: remCalc(24), md: remCalc(32)}}
                                borderRadius="full"
                            />
                        </Flex>
                    </VStack>
                    {!hideAccount && (
                        <ChakraSkeleton boxSize={remCalc(24)} borderRadius="full" flexShrink={0} />
                    )}
                </Flex>
            </VStack>
        </VStack>
    )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
const ProductHeader = ({name, product, firstPrimaryProduct, isFlexibleBundle = false}) => {
    const hideAccountPref = useBooleanCustomPreference('hideAccount')
    const hideAccount = hideAccountPref !== null ? hideAccountPref : false
    const [hasVisibleReview, setHasVisibleReview] = useState(false)
    const style = useMultiStyleConfig('ProductHeader', {
        hasVisibleReview: hasVisibleReview,
    })
    const {data: wishlist} = useWishList()
    const {formatMessage} = useIntl()
    const isFavouriteLoading = useRef(false)
    const shortDescriptionRef = useRef(null)
    const [isShortDescriptionExpanded, setIsShortDescriptionExpanded] = useState(false)
    const [isShortDescriptionOverflowing, setIsShortDescriptionOverflowing] = useState(false)
    const {toggleWishlistItem, isProductInWishlist} = useWishlistActions(wishlist)
    const isInWishlist = isProductInWishlist(product?.c_subModelIdentifier)
    const localizedProductName =
        product?.c_variantIdentifier || product?.name || product?.productName
    const isFavourite = isProductInWishlist(product?.c_subModelIdentifier ?? product?.productId)
    const productHeaderDetailsHtml = [product?.longDescription].filter(Boolean).join('')
    const normalizedProductHeaderDetailsHtml =
        normalizeProductHeaderDetailsHtml(productHeaderDetailsHtml)
    const productHeaderPreviewText = getProductHeaderPreviewText(productHeaderDetailsHtml)
    const firstPrimaryProductMeta = firstPrimaryProduct
        ? [
              firstPrimaryProduct?.c_productSubtitle,
              firstPrimaryProduct?.c_marketingColour,
              firstPrimaryProduct?.c_subModelIdentifier || firstPrimaryProduct?.id,
          ]
              .filter(Boolean)
              .join(' - ')
        : null

    const productMeta = firstPrimaryProductMeta
        ? firstPrimaryProductMeta
        : [
              product?.c_productSubtitle,
              product?.c_marketingColour,
              product?.c_subModelIdentifier || product?.id,
          ]
              .filter(Boolean)
              .join(' - ')

    const feefoReviewScoreId = firstPrimaryProduct
        ? firstPrimaryProduct?.c_primaryProductMasterId ||
          firstPrimaryProduct?.c_subModelIdentifier ||
          firstPrimaryProduct?.productId
        : product?.c_prdha_category || product?.c_subModelIdentifier || product?.id

    const bazaarReviewScoreId = firstPrimaryProduct
        ? firstPrimaryProduct?.c_subModelIdentifier || firstPrimaryProduct?.productId
        : product?.c_subModelIdentifier || product?.id

    useEffect(() => {
        if (isShortDescriptionExpanded) {
            setIsShortDescriptionOverflowing(false)
            return
        }

        const el = shortDescriptionRef.current
        if (!el) return

        const checkOverflow = () => {
            setIsShortDescriptionOverflowing(el.scrollHeight - el.clientHeight > 1)
        }

        const rafId = requestAnimationFrame(checkOverflow)

        const resizeObserver = new ResizeObserver(checkOverflow)
        resizeObserver.observe(el)

        return () => {
            cancelAnimationFrame(rafId)
            resizeObserver.disconnect()
        }
    }, [productHeaderPreviewText, isShortDescriptionExpanded])

    const {addLayer} = useGoogleTagManager()

    const scrollToReviewsSection = () => {
        if (typeof window === 'undefined') return

        const reviewsSection = document.getElementById('reviewsOnPage')
        if (!reviewsSection) return

        if (typeof reviewsSection.scrollIntoView === 'function') {
            reviewsSection.scrollIntoView({behavior: 'smooth', block: 'start'})
            return
        }

        const top = reviewsSection.getBoundingClientRect().top + window.pageYOffset
        window.scrollTo(0, top)
    }

    const handleReviewScoreKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            scrollToReviewsSection()
        }
    }

    const fireAddToWishlistEvent = () => {
        addLayer(polish_ecommerce_gtm_event.bind(this))
        addLayer(
            item_gtm_event.bind(this, {
                event: 'add_to_wishlist',
                currency: product?.currency,
                items: [
                    {
                        item_name: product.c_defaultName || '',
                        item_id: product?.productId || product?.id,
                        item_category: product?.c_defaultCategoryName,
                        item_variant: product?.c_subModelIdentifier,
                        price: parseFloat(
                            (product?.price / (1 + parseFloat(product?.c_taxRate) / 100)).toFixed(
                                2,
                            ),
                        ),
                        fullprice: getPriceData(product)?.isOnSale ? 'reduced' : 'fullprice',
                        rating_points: '',
                        erp_category: product?.c_materialGroup,
                        prdha_category: product?.c_prdhaCategory || product?.c_prdha_category,
                        stock_status:
                            product?.inventory?.stockLevel > 0 ? 'in-stock' : 'out-of-stock',
                    },
                ],
            }),
        )
    }

    useEffect(() => {
        setHasVisibleReview(false)
    }, [bazaarReviewScoreId, feefoReviewScoreId])

    return (
        <ChakraSkeleton isLoaded={name} w="100%" data-testid="product-header">
            <VStack {...style.container}>
                <Flex
                    {...style.reviewWrapper}
                    role="button"
                    tabIndex={0}
                    cursor="pointer"
                    onClick={scrollToReviewsSection}
                    onKeyDown={handleReviewScoreKeyDown}
                    aria-label={formatMessage({
                        id: 'product_detail.reviews.scroll_to_reviews',
                        defaultMessage: 'Go to reviews',
                    })}
                >
                    <ReviewScoreIcon
                        id={bazaarReviewScoreId}
                        parentSku={feefoReviewScoreId}
                        bvData={'rating_summary'}
                        data-cs-label="pdp-linkToReviews"
                        onVisibilityChange={setHasVisibleReview}
                    />
                </Flex>
                <VStack {...style.contentWrapper}>
                    <Flex justify="space-between" w="100%" alignItems="flex-start">
                        <Heading as="h1" {...style.title}>
                            {name}
                        </Heading>
                        {!hideAccount && !isBundleSet(product) && (
                            <IconButton
                                key="wishlist-button"
                                bg="transparent"
                                _hover={{bg: 'transparent'}}
                                _active={{bg: 'transparent'}}
                                paddingInlineStart={0}
                                paddingInlineEnd={0}
                                h="auto"
                                minWidth="auto"
                                data-cs-label="pdp-favourite"
                                data-testid="wishlist-button"
                                icon={
                                    isInWishlist ? (
                                        <FavouriteFilledIcon
                                            {...style.favSvgSolid}
                                            data-product-code={product?.id}
                                        />
                                    ) : (
                                        <FavouriteIcon
                                            {...style.favSvg}
                                            data-product-code={product?.id}
                                        />
                                    )
                                }
                                onClick={async () => {
                                    await toggleWishlistItem(product, isFavouriteLoading)
                                    if (!isInWishlist) {
                                        fireAddToWishlistEvent()
                                    }
                                }}
                                className="js-wishlist-add"
                                data-product-code={product?.id}
                                aria-label={
                                    isFavourite
                                        ? formatMessage(
                                              {
                                                  id: 'product_tile.assistive_msg.remove_from_wishlist',
                                                  defaultMessage: 'Remove {product} from wishlist',
                                              },
                                              {product: localizedProductName},
                                          )
                                        : formatMessage(
                                              {
                                                  id: 'product_tile.assistive_msg.add_to_wishlist',
                                                  defaultMessage: 'Add {product} to wishlist',
                                              },
                                              {product: localizedProductName},
                                          )
                                }
                            ></IconButton>
                        )}
                    </Flex>
                    {productMeta && (
                        <Text as="span" {...style.description}>
                            {productMeta}
                        </Text>
                    )}

                    {productHeaderDetailsHtml && (
                        <>
                            {isShortDescriptionExpanded ? (
                                <>
                                    <SanitizedHtml
                                        as={Box}
                                        html={normalizedProductHeaderDetailsHtml}
                                        sanitize={true}
                                        {...style.shortDescription}
                                    />
                                    <Button
                                        onClick={() => setIsShortDescriptionExpanded(false)}
                                        aria-label={formatMessage({
                                            id: 'product_detail.longDescription.button.read_less',
                                            defaultMessage: 'Read less',
                                        })}
                                        data-testid="product-header-read-less"
                                        variant={'link-button'}
                                        size={'base'}
                                    >
                                        {formatMessage({
                                            id: 'product_detail.longDescription.button.read_less',
                                            defaultMessage: 'Read less',
                                        })}
                                    </Button>
                                </>
                            ) : (
                                <Text
                                    ref={shortDescriptionRef}
                                    noOfLines={2}
                                    {...style.shortDescription}
                                >
                                    {productHeaderPreviewText}
                                </Text>
                            )}
                            <Box {...style.readmoreWrapper}>
                                {isShortDescriptionOverflowing && !isShortDescriptionExpanded && (
                                    <Button
                                        onClick={() => setIsShortDescriptionExpanded(true)}
                                        aria-label={formatMessage({
                                            id: 'product_detail.longDescription.button.label',
                                            defaultMessage: 'Read more',
                                        })}
                                        data-testid="product-header-read-more"
                                        variant={'link-button'}
                                        size={'base'}
                                    >
                                        {formatMessage({
                                            id: 'product_detail.longDescription.button.label',
                                            defaultMessage: 'Read more',
                                        })}
                                    </Button>
                                )}
                            </Box>
                        </>
                    )}
                </VStack>
            </VStack>
        </ChakraSkeleton>
    )
}

ProductHeader.propTypes = {
    name: PropTypes.string,
    product: PropTypes.object,
    handleWishlistItem: PropTypes.func,
    isWishlistLoading: PropTypes.bool,
    firstPrimaryProduct: PropTypes.object,
    isFlexibleBundle: PropTypes.bool,
}

export default ProductHeader
